import { z } from 'zod';

/**
 * Studio Shots API client. Shapes match routes/packshots.ts:
 * lists are `{ items, total, page, pageSize }`, presets carry `key`, `slug`, and `label`,
 * uploads are multipart field `files` and return `{ jobs, rejections }`.
 */

export const JOB_STATUSES = [
  'queued',
  'processing',
  'sourcing',
  'awaiting_pick',
  'needs_review',
  'completed',
  'failed',
  'no_source_found',
] as const;

export const jobStatusSchema = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof jobStatusSchema>;

export const cleanupPolicySchema = z.enum(['auto', 'always', 'never']);
export type CleanupPolicy = z.infer<typeof cleanupPolicySchema>;

export const MAX_BATCH_FILES = 10;
export const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_PAGE_SIZE = 100;

const countField = z.number().int().nonnegative();

export const statusCountsSchema = z.object({
  queued: countField,
  processing: countField,
  sourcing: countField,
  awaiting_pick: countField,
  needs_review: countField,
  completed: countField,
  failed: countField,
  no_source_found: countField,
});
export type StatusCounts = z.infer<typeof statusCountsSchema>;

const presetInfoSchema = z.object({
  key: z.string().min(1),
  slug: z.string().min(1),
  label: z.string().min(1),
  alphaOut: z.boolean().optional(),
});
export type PresetInfo = z.infer<typeof presetInfoSchema>;

export const presetsSchema = z.object({
  cleanup: z.array(presetInfoSchema),
  cutout: z.array(presetInfoSchema),
  defaults: z.object({
    cleanupPolicy: cleanupPolicySchema,
    cleanupPreset: z.string().min(1),
    cutoutPreset: z.string().min(1),
  }),
});
export type PackshotPresets = z.infer<typeof presetsSchema>;

const isoDateSchema = z.string().min(1).nullable();

export const batchSchema = z.object({
  id: z.string().min(1),
  userId: z.string().min(1),
  name: z.string().nullable(),
  cleanupPolicy: cleanupPolicySchema,
  cleanupPreset: z.string().min(1),
  cutoutPreset: z.string().min(1),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
  counts: statusCountsSchema,
});
export type PackshotBatch = z.infer<typeof batchSchema>;

export const assessmentSchema = z.object({
  productPresent: z.boolean(),
  productCount: z.number().int().min(0),
  productDescription: z.string(),
  hasBackground: z.boolean(),
  hasDropShadow: z.boolean(),
  unwanted: z.object({
    sizeOverlays: z.boolean(),
    promoCallouts: z.boolean(),
    stickersOrBadges: z.boolean(),
    irrelevantGraphics: z.boolean(),
  }),
  removableProps: z.array(z.string()).optional().default([]),
  isolateProduct: z.boolean().optional().default(false),
  productNoun: z.string().optional().default(''),
  productName: z.string().min(1).max(80).optional(),
  confidence: z.number(),
  notes: z.string(),
});
export type Assessment = z.infer<typeof assessmentSchema>;

export const placementSchema = z.object({
  mode: z.enum(['full-height', 'fit-width']),
  scale: z.number(),
  bbox: z.object({
    left: z.number(),
    top: z.number(),
    width: z.number(),
    height: z.number(),
  }),
  drawW: z.number(),
  drawH: z.number(),
  x: z.number(),
  y: z.number(),
  lowResolution: z.boolean(),
  leanDegrees: z.number().nullable().optional(),
  straightenDegrees: z.number().optional(),
});
export type Placement = z.infer<typeof placementSchema>;

export const qaSchema = z.object({
  productIntact: z.boolean(),
  noShadow: z.boolean(),
  noUnwantedGraphics: z.boolean(),
  edgesClean: z.boolean(),
  notCutOff: z.boolean(),
  issues: z.array(z.string()),
  deterministicOk: z.boolean(),
});
export type QaResult = z.infer<typeof qaSchema>;

const providerExchangeSchema = z.object({
  request: z.unknown(),
  response: z.unknown(),
});

const providerRunSchema = z.object({
  stage: z.string(),
  preset: z.string(),
  slug: z.string(),
  predictionId: z.string().optional(),
  startedAt: z.string(),
  durationMs: z.number().nonnegative(),
  status: z.string(),
  exchanges: z.array(providerExchangeSchema).optional(),
});
export type ProviderRun = z.infer<typeof providerRunSchema>;

export const jobErrorSchema = z.object({
  code: z.string(),
  stage: z.string(),
  message: z.string(),
  predictionId: z.string().optional(),
});
export type JobError = z.infer<typeof jobErrorSchema>;

export const fileLinksSchema = z.object({
  original: z.string().nullable(),
  normalized: z.string().nullable(),
  cleaned: z.string().nullable(),
  erased: z.string().nullable(),
  cutout: z.string().nullable(),
  output: z.string().nullable(),
});
export type JobFileLinks = z.infer<typeof fileLinksSchema>;

export const jobCardSchema = z.object({
  id: z.string().min(1),
  batchId: z.string().min(1),
  userId: z.string().min(1),
  ownerEmail: z.string().min(1).nullable(),
  sourceType: z.enum(['upload', 'web']),
  originalName: z.string().nullable(),
  productQuery: z.string().nullable(),
  productName: z.string().min(1).max(80).nullable(),
  status: jobStatusSchema,
  stage: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  error: jobErrorSchema.nullable(),
  placement: placementSchema.nullable(),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
  startedAt: isoDateSchema,
  completedAt: isoDateSchema,
  files: fileLinksSchema,
});
export type PackshotJobCard = z.infer<typeof jobCardSchema>;

export const sourceMetaSchema = z.object({
  width: z.number(),
  height: z.number(),
  hasAlpha: z.boolean(),
  alphaCoverage: z.number(),
  cornerUniformity: z.boolean(),
  mime: z.string(),
});
export type SourceMeta = z.infer<typeof sourceMetaSchema>;

export const jobSchema = jobCardSchema.extend({
  assessment: assessmentSchema.nullable(),
  qa: qaSchema.nullable(),
  providerRuns: z.array(providerRunSchema),
  sourceMeta: sourceMetaSchema.nullable(),
});
export type PackshotJob = z.infer<typeof jobSchema>;

const pageFields = {
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
};

const batchPageSchema = z.object({
  items: z.array(batchSchema),
  ...pageFields,
});
export type BatchPage = z.infer<typeof batchPageSchema>;

const jobPageSchema = z.object({
  items: z.array(jobCardSchema),
  ...pageFields,
});
export type JobPage = z.infer<typeof jobPageSchema>;

const uploadRejectionSchema = z.object({
  name: z.string(),
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
  }),
});
export type UploadRejection = z.infer<typeof uploadRejectionSchema>;

const createJobsSchema = z.object({
  jobs: z.array(jobCardSchema),
  rejections: z.array(uploadRejectionSchema),
});
export type CreateJobsResult = z.infer<typeof createJobsSchema>;

const pageImageFileSchema = z.string().regex(/^\/api\/packshots\/page-images\/[0-9a-f-]+\/files\/[0-9a-f-]+$/i);

const pageImageSetSchema = z.object({
  id: z.string().uuid(),
  minShortSide: z.number().int().positive(),
  truncated: z.boolean(),
  images: z.array(z.object({
    id: z.string().uuid(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    name: z.string().min(1),
    file: pageImageFileSchema,
  })),
});
export type PageImageSet = z.infer<typeof pageImageSetSchema>;

const pageJobsSchema = createJobsSchema.extend({
  batchId: z.string().uuid(),
});
export type PageJobsResult = z.infer<typeof pageJobsSchema>;

export type RetryJobInput = {
  cleanupPreset?: string;
  cleanupPolicy?: CleanupPolicy;
  cutoutPreset?: string;
};

export type CreateBatchInput = {
  cleanupPolicy?: CleanupPolicy;
  cleanupPreset?: string;
  cutoutPreset?: string;
};

export type SkippedFile = {
  name: string;
  error: string;
};

const ACCEPTED_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/tiff',
  'image/avif',
]);

const ACCEPTED_EXTENSION = /\.(jpe?g|png|webp|gif|tiff?|avif)$/i;

export function fileRejection(file: File): string | null {
  const name = file.name || 'Untitled';
  if (/\.svg$/i.test(name) || file.type === 'image/svg+xml') return 'SVG not supported';
  if (file.size <= 0) return 'file is empty';
  if (file.size > MAX_FILE_BYTES) return 'larger than 25 MB';
  const typeOk = file.type.length > 0 && ACCEPTED_TYPES.has(file.type);
  const extensionOk = ACCEPTED_EXTENSION.test(name);
  if (!typeOk && !extensionOk) return 'not a supported image';
  return null;
}

export function partitionUploadFiles(files: readonly File[]): { accepted: File[]; skipped: SkippedFile[] } {
  const accepted: File[] = [];
  const skipped: SkippedFile[] = [];
  for (const file of files) {
    const name = file.name || 'Untitled';
    const error = fileRejection(file);
    if (error) {
      skipped.push({ name, error });
      continue;
    }
    if (accepted.length >= MAX_BATCH_FILES) {
      skipped.push({ name, error: 'only 10 images per batch' });
      continue;
    }
    accepted.push(file);
  }
  return { accepted, skipped };
}

export function findGptImagePreset(presets: PresetInfo[]): PresetInfo | undefined {
  return presets.find((preset) => preset.key === 'gpt-image-1.5' || preset.slug === 'openai/gpt-image-1.5');
}

export function findBriaPreset(presets: PresetInfo[]): PresetInfo | undefined {
  return presets.find((preset) => {
    const key = preset.key.toLowerCase();
    const slug = preset.slug.toLowerCase();
    return key === 'bria' || slug.includes('bria');
  });
}

export function jobFileUrl(
  jobId: string,
  kind: 'original' | 'normalized' | 'cleaned' | 'erased' | 'cutout' | 'output',
  version?: string | null,
  options?: { download?: boolean },
): string {
  const path = `/api/packshots/jobs/${encodeURIComponent(jobId)}/files/${kind}`;
  const params = new URLSearchParams();
  if (version) params.set('v', version);
  if (options?.download) params.set('download', '1');
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function packshotDownloadName(name: string): string {
  const stem = name
    .replace(/\.(png|jpe?g|webp|gif)$/i, '')
    .replace(/[^\w.\- ]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 80);
  return `${stem || 'packshot'}.png`;
}

export async function downloadJobOutput(jobId: string, name: string, version: string | null): Promise<void> {
  if (!jobId) throw new Error('Job id is required');
  const response = await fetch(jobFileUrl(jobId, 'output', version, { download: true }), {
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('Could not download this image');
  const blob = await response.blob();
  if (blob.size < 8 || blob.type.includes('text/html')) throw new Error('Could not download this image');
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = packshotDownloadName(name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function messageFromBody(data: unknown, fallback: string): string {
  const record = asRecord(data);
  if (!record) return fallback;
  const error = readString(record.error);
  if (error) return error;
  const message = readString(record.message);
  if (message) return message;
  const nested = asRecord(record.error);
  const nestedMessage = nested ? readString(nested.message) : null;
  if (nestedMessage) return nestedMessage;
  return fallback;
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new Error('Response was not JSON');
  }
}

async function apiJson(path: string, init: RequestInit = {}): Promise<unknown> {
  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (!isForm && init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  const data = await readBody(res);
  if (!res.ok) {
    throw new Error(messageFromBody(data, `Request failed (${res.status})`));
  }
  return data;
}

function parseOrThrow<T>(schema: z.ZodType<T, z.ZodTypeDef, unknown>, data: unknown, label: string): T {
  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const path = issue ? issue.path.join('.') : '';
    throw new Error(path ? `${label} (${path})` : label);
  }
  return parsed.data;
}

function assertPage(page: number, pageSize: number): void {
  if (!Number.isInteger(page) || page < 1) throw new Error('page must be at least 1');
  if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > MAX_PAGE_SIZE) {
    throw new Error('pageSize must be from 1 to 100');
  }
}

export async function fetchPresets(): Promise<PackshotPresets> {
  const data = await apiJson('/api/packshots/presets');
  return parseOrThrow(presetsSchema, data, 'Unexpected presets response');
}

async function fetchBatchPage(scope: 'mine' | 'all', page: number, pageSize: number): Promise<BatchPage> {
  assertPage(page, pageSize);
  const params = new URLSearchParams({
    scope,
    page: String(page),
    pageSize: String(pageSize),
  });
  const data = await apiJson(`/api/packshots/batches?${params.toString()}`);
  return parseOrThrow(batchPageSchema, data, 'Unexpected batch list');
}

export async function listRecentJobs(
  scope: 'mine' | 'all',
  page: number,
  pageSize: number,
  filter?: { q?: string; status?: JobStatus },
): Promise<JobPage> {
  assertPage(page, pageSize);
  const params = new URLSearchParams({
    scope,
    page: String(page),
    pageSize: String(pageSize),
  });
  const query = filter?.q?.trim();
  if (query) params.set('q', query);
  if (filter?.status) params.set('status', filter.status);
  const data = await apiJson(`/api/packshots/jobs?${params.toString()}`);
  return parseOrThrow(jobPageSchema, data, 'Unexpected job list');
}

export async function listBatches(scope: 'mine' | 'all', pageSize: number): Promise<BatchPage> {
  if (!Number.isInteger(pageSize) || pageSize < 1) throw new Error('pageSize must be at least 1');
  const requestSize = Math.min(pageSize, MAX_PAGE_SIZE);
  const items: PackshotBatch[] = [];
  let page = 1;
  let total = 0;
  while (items.length < pageSize && page <= MAX_PAGE_SIZE) {
    const next = await fetchBatchPage(scope, page, requestSize);
    total = next.total;
    items.push(...next.items);
    if (next.items.length < requestSize || items.length >= total) break;
    page += 1;
  }
  return {
    items: items.slice(0, pageSize),
    total,
    page: 1,
    pageSize: requestSize,
  };
}

export async function createBatch(input: CreateBatchInput): Promise<PackshotBatch> {
  const body: CreateBatchInput = {};
  if (input.cleanupPolicy) body.cleanupPolicy = input.cleanupPolicy;
  if (input.cleanupPreset) body.cleanupPreset = input.cleanupPreset;
  if (input.cutoutPreset) body.cutoutPreset = input.cutoutPreset;
  const data = await apiJson('/api/packshots/batches', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return parseOrThrow(batchSchema, data, 'Unexpected batch response');
}

export async function getBatch(batchId: string): Promise<PackshotBatch> {
  if (!batchId) throw new Error('Batch id is required');
  const data = await apiJson(`/api/packshots/batches/${encodeURIComponent(batchId)}`);
  return parseOrThrow(batchSchema, data, 'Unexpected batch response');
}

export async function uploadBatchJobs(batchId: string, files: readonly File[]): Promise<CreateJobsResult> {
  if (!batchId) throw new Error('Batch id is required');
  if (files.length === 0) throw new Error('Choose at least one image');
  if (files.length > MAX_BATCH_FILES) throw new Error('Only 10 images can be uploaded at once');
  const form = new FormData();
  for (const file of files) form.append('files', file);
  const data = await apiJson(`/api/packshots/batches/${encodeURIComponent(batchId)}/jobs`, {
    method: 'POST',
    body: form,
  });
  return parseOrThrow(createJobsSchema, data, 'Unexpected upload response');
}

async function listJobPage(batchId: string, page: number, pageSize: number): Promise<JobPage> {
  assertPage(page, pageSize);
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const data = await apiJson(`/api/packshots/batches/${encodeURIComponent(batchId)}/jobs?${params.toString()}`);
  return parseOrThrow(jobPageSchema, data, 'Unexpected job list');
}

export async function listBatchJobs(batchId: string): Promise<PackshotJobCard[]> {
  if (!batchId) throw new Error('Batch id is required');
  const pageSize = MAX_PAGE_SIZE;
  const first = await listJobPage(batchId, 1, pageSize);
  const items = [...first.items];
  let page = first.page;
  while (items.length < first.total && page < MAX_PAGE_SIZE) {
    page += 1;
    const next = await listJobPage(batchId, page, pageSize);
    if (next.items.length === 0) break;
    items.push(...next.items);
    if (next.items.length < pageSize) break;
  }
  if (items.length < first.total && page >= MAX_PAGE_SIZE) {
    throw new Error('Job list did not finish');
  }
  return items;
}

export async function getJob(jobId: string): Promise<PackshotJob> {
  if (!jobId) throw new Error('Job id is required');
  const data = await apiJson(`/api/packshots/jobs/${encodeURIComponent(jobId)}`);
  return parseOrThrow(jobSchema, data, 'Unexpected job response');
}

export async function retryJob(jobId: string, input: RetryJobInput): Promise<void> {
  if (!jobId) throw new Error('Job id is required');
  const body: RetryJobInput = {};
  if (input.cleanupPreset) body.cleanupPreset = input.cleanupPreset;
  if (input.cleanupPolicy) body.cleanupPolicy = input.cleanupPolicy;
  if (input.cutoutPreset) body.cutoutPreset = input.cutoutPreset;
  await apiJson(`/api/packshots/jobs/${encodeURIComponent(jobId)}/retry`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function straightenJob(jobId: string, degrees: number): Promise<void> {
  if (!jobId) throw new Error('Job id is required');
  if (!Number.isFinite(degrees)) throw new Error('Straighten angle is required');
  await apiJson(`/api/packshots/jobs/${encodeURIComponent(jobId)}/straighten`, {
    method: 'POST',
    body: JSON.stringify({ degrees }),
  });
}

export async function approveJob(jobId: string): Promise<void> {
  if (!jobId) throw new Error('Job id is required');
  await apiJson(`/api/packshots/jobs/${encodeURIComponent(jobId)}/approve`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function deleteJob(jobId: string): Promise<void> {
  if (!jobId) throw new Error('Job id is required');
  await apiJson(`/api/packshots/jobs/${encodeURIComponent(jobId)}`, { method: 'DELETE' });
}

export async function findPageImages(url: string): Promise<PageImageSet> {
  const trimmed = url.trim();
  if (!trimmed) throw new Error('Enter a page address');
  const data = await apiJson('/api/packshots/page-images', {
    method: 'POST',
    body: JSON.stringify({ url: trimmed }),
  });
  return parseOrThrow(pageImageSetSchema, data, 'Unexpected page image response');
}

export async function createJobsFromPage(input: {
  importId: string;
  imageIds: readonly string[];
  cleanupPolicy?: CleanupPolicy;
  cleanupPreset?: string;
  cutoutPreset?: string;
}): Promise<PageJobsResult> {
  if (!input.importId) throw new Error('Page import is missing');
  if (input.imageIds.length === 0) throw new Error('Choose at least one image');
  if (input.imageIds.length > MAX_BATCH_FILES) throw new Error('Only 10 images can be added at once');
  const body: {
    imageIds: string[];
    cleanupPolicy?: CleanupPolicy;
    cleanupPreset?: string;
    cutoutPreset?: string;
  } = { imageIds: [...input.imageIds] };
  if (input.cleanupPolicy) body.cleanupPolicy = input.cleanupPolicy;
  if (input.cleanupPreset) body.cleanupPreset = input.cleanupPreset;
  if (input.cutoutPreset) body.cutoutPreset = input.cutoutPreset;
  const data = await apiJson(`/api/packshots/page-images/${encodeURIComponent(input.importId)}/jobs`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return parseOrThrow(pageJobsSchema, data, 'Unexpected page job response');
}

export async function retryFailedBatch(batchId: string): Promise<void> {
  if (!batchId) throw new Error('Batch id is required');
  await apiJson(`/api/packshots/batches/${encodeURIComponent(batchId)}/retry-failed`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
